import React, { useEffect, useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import ContributorRowAvatar from '../../components/contributors/ContributorRowAvatar';
import Skeleton from '../../components/ui/Skeleton';
import {
  memberNameClass,
  teamMemberRowClass,
  teamSectionHeaderClass,
  teamSectionTitleClass,
} from '../../components/members/memberViewTheme';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { contributorAPI } from '../../services/api';
import { normalizeContributorRows } from '../../utils/contributorChartRows';

function formatLastCommitDate(iso) {
  if (iso == null || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function TeamRosterSkeleton({ rows = 6 }: any) {
  return (
    <ul className="list-none m-0 p-0" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className={`${teamMemberRowClass} gap-3`}>
          <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-48" />
          </div>
          <Skeleton className="h-4 w-20 shrink-0" />
        </li>
      ))}
    </ul>
  );
}

/**
 * @param {{
 *   orgId: string,
 *   streamId: string,
 *   projectId: string,
 *   repositories: Array<Record<string, unknown>>,
 * }} props
 */
export default function PortfolioTeamPanel({ orgId, streamId, projectId, repositories }: any) {
  const canReadContributors = useFeatureFlag('api.projects.contributors.read', false);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const hasRepos = Array.isArray(repositories) && repositories.length > 0;

  useEffect(() => {
    if (!canReadContributors || !orgId || !streamId || !projectId || !hasRepos) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    contributorAPI
      .getByProject(orgId, streamId, projectId, { status: 'all' })
      .then((list) => {
        if (!cancelled) {
          setRows(normalizeContributorRows(Array.isArray(list) ? list : []));
        }
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canReadContributors, orgId, streamId, projectId, hasRepos]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => Number(b.totalCommits) - Number(a.totalCommits));
    return copy;
  }, [rows]);

  if (!canReadContributors) {
    return (
      <div className="rounded-md border border-dark-border bg-dark-base/20 p-4 text-sm text-slate-400">
        Contributor data is unavailable for your organisation.
      </div>
    );
  }

  return (
    <div className="card-mdl-static overflow-hidden">
      <div className={teamSectionHeaderClass}>
        <div>
          <h4 className={`${teamSectionTitleClass} inline-flex items-center gap-2`}>
            <Users className="h-4 w-4 text-vivid-red" aria-hidden />
            Team{' '}
            {!loading && sortedRows.length > 0 ? (
              <span className="font-normal text-slate-500 tabular-nums">({sortedRows.length})</span>
            ) : null}
          </h4>
          <p className="mt-0.5 text-xs text-slate-500">
            Contributors across connected repositories
          </p>
        </div>
      </div>

      {!hasRepos ? (
        <p className="px-4 py-10 text-center text-sm text-slate-500">
          Connect repositories to this project to see contributors.
        </p>
      ) : loading ? (
        <TeamRosterSkeleton />
      ) : sortedRows.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-slate-500">
          No contributor data yet. Sync repositories to load commits.
        </p>
      ) : (
        <ul className="list-none m-0 p-0">
          {sortedRows.map((row) => {
            const lastCommitLabel = formatLastCommitDate(row.lastCommitDate);
            const emailStr = String(row.displayEmail || '').trim();
            const githubStr = String(row.github || '').trim();
            const secondary = emailStr || (githubStr ? `@${githubStr}` : '');
            const commits = Number(row.totalCommits) || 0;

            return (
              <li key={row._rowKey} className={teamMemberRowClass}>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <ContributorRowAvatar row={row} size="sm" />
                  <div className="min-w-0">
                    <div className={`${memberNameClass} truncate`} title={row.displayName}>
                      {row.displayName}
                    </div>
                    {secondary ? (
                      <div className="truncate text-xs text-slate-400" title={secondary}>
                        {secondary}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 text-right sm:flex-row sm:items-center sm:gap-3">
                  <div className="text-xs text-slate-500 tabular-nums">
                    <span className="text-slate-400">{commits}</span> commit
                    {commits === 1 ? '' : 's'}
                    {lastCommitLabel ? (
                      <>
                        {' · '}
                        <span className="text-slate-400" title={`Last commit: ${lastCommitLabel}`}>
                          {lastCommitLabel}
                        </span>
                      </>
                    ) : null}
                  </div>
                  {row.isActive === false ? (
                    <span className="rounded bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-400">
                      Inactive
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
